import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { ChangeLogView } from "../../features/change-log/ChangeLogView.js";
import {
  type ChangeLogFilterState,
  CHANGE_LOG_PAGE_SIZE,
  EMPTY_CHANGE_LOG_FILTERS,
  toChangeLogQueryFilters,
} from "../../features/change-log/changeLogTypes.js";
import {
  useProjectChangeLogFacetsQuery,
  useProjectChangeLogQuery,
} from "./ProjectChangeLog.generated.js";
import { useProjectEntityStrategy } from "./useProjectEntityStrategy.js";

interface Props {
  projectId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

/**
 * The project change-log page: hosts the shared `ChangeLogView` over the unioned
 * `projectChangeLog` / `projectChangeLogFacets` data (project row + its documents),
 * with the project entity-render strategy. Reached from the project submenu, so it
 * carries the standard project page chrome (breadcrumbs + submenu) like its siblings.
 */
export function ProjectChangeLog({
  projectId,
  prefixBreadcrumbs,
  submenuItems,
  activeSubmenuItem,
}: Props) {
  const { t } = useTranslate(["Global"]);
  const client = useApolloClient();

  const [filters, setFilters] = React.useState<ChangeLogFilterState>(
    EMPTY_CHANGE_LOG_FILTERS
  );
  const queryFilters = toChangeLogQueryFilters(filters);

  const facetsQuery = useProjectChangeLogFacetsQuery({
    client,
    variables: { projectId },
  });
  const logQuery = useProjectChangeLogQuery({
    client,
    variables: {
      projectId,
      filters: queryFilters,
      first: CHANGE_LOG_PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true,
  });

  const connection = getDataOrNull(logQuery.data?.projectChangeLog);
  const facets = getDataOrNull(facetsQuery.data?.projectChangeLogFacets);
  const missingPermissions =
    logQuery.data?.projectChangeLog.__typename === "MissingPermissions";

  const entityStrategy = useProjectEntityStrategy(projectId);
  const pageInfo = connection?.pageInfo;

  const breadcrumbs = React.useMemo(
    () => [
      ...prefixBreadcrumbs,
      {
        title: t("Change log", { ns: "Global" }),
        to: `/projects/${projectId}/change-log`,
      },
    ],
    [prefixBreadcrumbs, t, projectId]
  );

  return (
    <Page
      subtitle={prefixBreadcrumbs[0]?.title}
      title={t("Change log", { ns: "Global" })}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      header={
        <PageHeader
          breadcrumbs={breadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
    >
      <PageContainer>
        <ChangeLogView
          entries={(connection?.edges ?? []).map(e => e.node)}
          facets={facets}
          loading={logQuery.loading}
          error={logQuery.error?.message}
          missingPermissions={missingPermissions}
          hasNextPage={Boolean(pageInfo?.hasNextPage)}
          onFetchMore={async () => {
            if (!pageInfo?.hasNextPage) return;
            await logQuery.fetchMore({
              variables: {
                after: pageInfo.endCursor,
                first: CHANGE_LOG_PAGE_SIZE,
              },
            });
          }}
          filters={filters}
          setFilters={setFilters}
          entityStrategy={entityStrategy}
        />
      </PageContainer>
    </Page>
  );
}
